import { box, type Box } from '../geometry/box';
import { DEFAULT_CAMERA_BACKGROUND, parseCameraBackground, type CameraBackground } from './background';
import { computeFrameLayout, computeOutputSize, containRect, resolveAspect, type FrameLayout, type LayoutOptions, type Size } from '../geometry/framing';

/**
 * The camera of a screen recording is kept as its own layer, so where it sits
 * can still change after recording: a bubble over the screen, a split frame
 * (camera and screen side by side, or stacked for vertical video), or hidden.
 */
export type CameraLayout = 'bubble' | 'split' | 'off';
export type CameraCorner = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
export type CameraShape = 'circle' | 'rounded';
/** Split frames: the camera first (top, or left in landscape) or the screen first. */
export type SplitOrder = 'camera-first' | 'screen-first';

export const CAMERA_LAYOUTS: readonly CameraLayout[] = ['bubble', 'split', 'off'];
export const CAMERA_CORNERS: readonly CameraCorner[] = ['top-left', 'top-right', 'bottom-left', 'bottom-right'];
export const CAMERA_SHAPES: readonly CameraShape[] = ['circle', 'rounded'];

export interface CameraSettings {
  readonly layout: CameraLayout;
  readonly corner: CameraCorner;
  /** Bubble size as a share of the frame's short edge. */
  readonly size: number;
  readonly shape: CameraShape;
  readonly order: SplitOrder;
  /** Blur or replace the room behind the person. */
  readonly background: CameraBackground;
}

export const CAMERA_SIZE = { min: 0.16, max: 0.42 } as const;

export const DEFAULT_CAMERA_SETTINGS: CameraSettings = {
  layout: 'bubble',
  corner: 'bottom-right',
  size: 0.26,
  shape: 'circle',
  order: 'camera-first',
  background: DEFAULT_CAMERA_BACKGROUND,
};

/** Where the camera lands on the output frame (output pixels) and how it is cut out. */
export interface CameraPlacement {
  readonly rect: Box;
  /** `panel` is a split-frame region: square edges, no shadow. */
  readonly shape: CameraShape | 'panel';
  /** Corner radius in output pixels (half the size for a circle). */
  readonly radius: number;
}

/** Distance between a bubble and the frame edge, as a share of the short edge. */
const BUBBLE_MARGIN = 0.04;
/** Rounded bubbles: corner radius as a share of the bubble size. */
const ROUNDED_RADIUS = 0.22;
/** A split frame never gives the screen more than this share, so the camera stays a real part of it. */
const MAX_SCREEN_SHARE = { stacked: 0.62, sideBySide: 0.68 } as const;

const clampSize = (size: number): number => Math.min(CAMERA_SIZE.max, Math.max(CAMERA_SIZE.min, size));

export function bubblePlacement(settings: Pick<CameraSettings, 'corner' | 'size' | 'shape'>, output: Size): CameraPlacement {
  const short = Math.min(output.width, output.height);
  const size = Math.round(clampSize(settings.size) * short);
  const margin = Math.round(BUBBLE_MARGIN * short);
  const left = settings.corner.endsWith('left');
  const top = settings.corner.startsWith('top');
  const rect = box(left ? margin : output.width - margin - size, top ? margin : output.height - margin - size, size, size);
  return { rect, shape: settings.shape, radius: settings.shape === 'circle' ? size / 2 : Math.round(size * ROUNDED_RADIUS) };
}

export interface SplitFrame {
  /** The region the screen recording is fitted into (output pixels). */
  readonly screen: Box;
  readonly camera: Box;
}

/**
 * Divides a frame between screen and camera along its long axis: stacked for
 * portrait and square frames, side by side for landscape ones. The screen keeps
 * its whole picture (text must stay readable), so it takes the space its aspect
 * needs, up to a cap; the camera fills the rest.
 */
export function splitFrame(output: Size, screenAspect: number, order: SplitOrder): SplitFrame {
  const stacked = output.height >= output.width;
  const cameraFirst = order === 'camera-first';
  if (stacked) {
    const screenHeight = Math.round(Math.min(output.width / screenAspect, output.height * MAX_SCREEN_SHARE.stacked));
    const cameraHeight = output.height - screenHeight;
    return cameraFirst
      ? { camera: box(0, 0, output.width, cameraHeight), screen: box(0, cameraHeight, output.width, screenHeight) }
      : { screen: box(0, 0, output.width, screenHeight), camera: box(0, screenHeight, output.width, cameraHeight) };
  }
  const screenWidth = Math.round(Math.min(output.height * screenAspect, output.width * MAX_SCREEN_SHARE.sideBySide));
  const cameraWidth = output.width - screenWidth;
  return cameraFirst
    ? { camera: box(0, 0, cameraWidth, output.height), screen: box(cameraWidth, 0, screenWidth, output.height) }
    : { screen: box(0, 0, screenWidth, output.height), camera: box(screenWidth, 0, cameraWidth, output.height) };
}

/**
 * Frame layout of the screen inside a split frame. The frame size follows the
 * chosen aspect at full quality (built around the source, like a blurred fill),
 * and the screen sits whole in its region over a blurred copy of itself.
 */
export function splitLayout(source: Size, options: Pick<LayoutOptions, 'aspect' | 'resolution'>, order: SplitOrder): FrameLayout {
  const aspect = resolveAspect(options.aspect, source);
  const output = computeOutputSize(source, aspect, options.resolution, 'blurFill');
  const { screen } = splitFrame(output, source.width / source.height, order);
  const inner = containRect({ width: screen.w, height: screen.h }, source.width / source.height);
  return {
    output,
    sourceRect: box(0, 0, source.width, source.height),
    destRect: box(Math.round(screen.x + inner.x), Math.round(screen.y + inner.y), Math.round(inner.w), Math.round(inner.h)),
    fit: 'blurFill',
  };
}

/**
 * The frame layout of a video given its camera layer (null when it has none): a
 * split frame fits the whole screen beside the camera; otherwise the usual crop or fill.
 */
export function layoutWithCamera(source: Size, format: LayoutOptions, camera: CameraSettings | null): FrameLayout {
  return camera?.layout === 'split' ? splitLayout(source, format, camera.order) : computeFrameLayout(source, format);
}

/** Where the camera goes for these settings, or null when it is hidden. */
export function cameraPlacement(settings: CameraSettings, output: Size, screenAspect: number): CameraPlacement | null {
  if (settings.layout === 'off') return null;
  if (settings.layout === 'bubble') return bubblePlacement(settings, output);
  return { rect: splitFrame(output, screenAspect, settings.order).camera, shape: 'panel', radius: 0 };
}

/**
 * Centered crop of the camera picture (camera pixels) with the aspect of the
 * spot it fills, so a face is never squashed.
 */
export function coverCrop(source: Size, targetAspect: number): Box {
  const sourceAspect = source.width / source.height;
  if (targetAspect > sourceAspect) {
    const height = source.width / targetAspect;
    return box(0, (source.height - height) / 2, source.width, height);
  }
  const width = source.height * targetAspect;
  return box((source.width - width) / 2, 0, width, source.height);
}

/** The corner nearest to a point on the frame (output pixels): where a dragged bubble settles. */
export function nearestCorner(point: { readonly x: number; readonly y: number }, output: Size): CameraCorner {
  const vertical = point.y < output.height / 2 ? 'top' : 'bottom';
  const horizontal = point.x < output.width / 2 ? 'left' : 'right';
  return `${vertical}-${horizontal}`;
}

/** Whether a point (output pixels) is on the camera, honouring a circle's round edge. */
export function hitsPlacement(placement: CameraPlacement, point: { readonly x: number; readonly y: number }): boolean {
  const { rect } = placement;
  if (point.x < rect.x || point.y < rect.y || point.x > rect.x + rect.w || point.y > rect.y + rect.h) return false;
  if (placement.shape !== 'circle') return true;
  const dx = point.x - (rect.x + rect.w / 2);
  const dy = point.y - (rect.y + rect.h / 2);
  return dx * dx + dy * dy <= (rect.w / 2) * (rect.w / 2);
}

/** Restores settings from storage (presets saved before the camera existed, or edited by hand). */
export function parseCameraSettings(value: unknown): CameraSettings {
  const raw = (typeof value === 'object' && value !== null ? value : {}) as Partial<Record<keyof CameraSettings, unknown>>;
  const pick = <T extends string>(candidate: unknown, allowed: readonly T[], fallback: T): T =>
    allowed.includes(candidate as T) ? (candidate as T) : fallback;
  return {
    layout: pick(raw.layout, CAMERA_LAYOUTS, DEFAULT_CAMERA_SETTINGS.layout),
    corner: pick(raw.corner, CAMERA_CORNERS, DEFAULT_CAMERA_SETTINGS.corner),
    size: typeof raw.size === 'number' && Number.isFinite(raw.size) ? clampSize(raw.size) : DEFAULT_CAMERA_SETTINGS.size,
    shape: pick(raw.shape, CAMERA_SHAPES, DEFAULT_CAMERA_SETTINGS.shape),
    order: pick(raw.order, ['camera-first', 'screen-first'] as const, DEFAULT_CAMERA_SETTINGS.order),
    background: parseCameraBackground(raw.background),
  };
}
